import { useAppStore } from "./app.store";
import { assembleSprintMissions } from "@/campaigns/modules/sprint-mission/assemble";
import { assembleMissions } from "@/campaigns/modules/missions/assemble";
import { assemblePrizeDrops } from "@/campaigns/modules/prizedrop/assemble";
import { assembleTournaments } from "@/campaigns/modules/tournament/assemble";
import { assembleJackpots } from "@/campaigns/modules/jackpot/assemble";
import { assembleChases } from "@/campaigns/modules/chase/assemble";
import { assembleRaffles } from "@/campaigns/modules/raffle/assemble";

/**
 * Structural equality for an assembled list. The assembled objects are plain data
 * (campaign is parsed WS JSON, prizes are mapped literals) with stable key order,
 * so a serialize-compare is reliable and the lists are small. The length
 * short-circuit skips serialization when the count already differs.
 */
function listEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Re-derive the assembled `prizeDrops` view array from the raw shared +
 * volatile slices. Called by the `campaigns` / `prize_drop_prizes` handlers
 * after they update their slice.
 *
 * `assemblePrizeDrops` returns fresh objects each call, so writing it
 * unconditionally would replace the `prizeDrops` reference on every push —
 * including the periodic `campaigns` keepalive, which carries identical data —
 * and re-render every consumer. Skip the write when nothing actually changed.
 */
export function recomputePrizeDrops(): void {
  const state = useAppStore.getState();
  const next = assemblePrizeDrops(
    state.campaignsById,
    state.prizeDropPrizesById
  );
  if (listEqual(state.prizeDrops, next)) return;
  state.setPrizeDrops(next);
}

/**
 * Re-derive the assembled `tournaments` view array from the shared `campaigns`
 * slice. Called by the `campaigns` handler after it updates the slice — NOT by the
 * leaderboard handler (the leaderboard lives in its own slice, read directly, so a
 * board tick never rebuilds this structural list; see `modules/tournament/types`).
 *
 * `assembleTournaments` returns fresh objects each call, so the change-detection
 * skip keeps the `tournaments` reference stable across the periodic `campaigns`
 * keepalive (identical data), avoiding a needless re-render of every consumer.
 */
export function recomputeTournaments(): void {
  const state = useAppStore.getState();
  const next = assembleTournaments(state.campaignsById, state.variant);
  if (listEqual(state.tournaments, next)) return;
  state.setTournaments(next);
}

/**
 * Re-derive the assembled `missionsCampaigns` view array from the raw shared +
 * volatile slices. Called by the `campaigns` / `mission_progress` handlers
 * after they update their slice. The change-detection skip matters doubly here:
 * `mission_progress` ticks ~every second, and most ticks carry unchanged data.
 */
export function recomputeMissions(): void {
  const state = useAppStore.getState();
  const next = assembleMissions(state.campaignsById, state.missionProgressById);
  if (listEqual(state.missionsCampaigns, next)) return;
  state.setMissionsCampaigns(next);
}

/**
 * Re-derive the assembled `sprintMissions` view array. Called by the `campaigns`,
 * `mission_progress` AND `sprint_mission_pool` handlers — a three-way join, one more stream
 * than any other type.
 *
 * The change-detection skip matters most here: `mission_progress` ticks ~every second and the
 * pool stream pushes on every claim across all players, so without it every consumer of this
 * list would re-render on data that mostly has not changed.
 */
export function recomputeSprintMissions(): void {
  const state = useAppStore.getState();
  const next = assembleSprintMissions(
    state.campaignsById,
    state.missionProgressById,
    state.sprintMissionPoolById
  );
  if (listEqual(state.sprintMissions, next)) return;
  state.setSprintMissions(next);
}

/**
 * Re-derive the assembled `jackpotCampaigns` view array — `campaigns` ⋈ `jackpot_progress`.
 * Called by both handlers.
 *
 * The change-detection skip matters here because the progress stream ticks every 30 s and
 * drains every 5 s on any bet's pot contribution: most pushes change one pot value, so most of
 * the time nothing else about the assembled list moves. Note that the clock is deliberately NOT
 * an input to assembly — `useJackpotClock` owns time — so a push with unchanged data is a no-op
 * here regardless of how much wall-clock time has passed.
 */
export function recomputeJackpots(): void {
  const state = useAppStore.getState();
  const next = assembleJackpots(state.campaignsById, state.jackpotProgressById);
  if (listEqual(state.jackpotCampaigns, next)) return;
  state.setJackpotCampaigns(next);
}

/**
 * Re-derive the assembled `chases` view array — `campaigns` ⋈ `tournament_progress`. Called by
 * both handlers. The progress stream pushes on every processed bet of the player (and every
 * 30 s), so the change-detection skip is what keeps a push that moved nothing from re-rendering
 * every chase consumer.
 */
export function recomputeChases(): void {
  const state = useAppStore.getState();
  const next = assembleChases(
    state.campaignsById,
    state.tournamentProgress.items
  );
  if (listEqual(state.chases, next)) return;
  state.setChases(next);
}

/** Re-derive every type's assembled view — after the shared campaign slice changed under them
 *  (a `campaigns` push, or a player's own end landing on `campaign_progress`). */
export function recomputeAssembledCampaigns(): void {
  recomputePrizeDrops();
  recomputeTournaments();
  recomputeMissions();
  recomputeSprintMissions();
  recomputeJackpots();
  recomputeChases();
  recomputeRaffles();
}

/**
 * Re-derive the assembled `raffles` view array — `campaigns` ⋈ `raffle_progress`. Called by both
 * handlers. The change-detection skip matters because the progress stream re-pushes every 30 s
 * with mostly unchanged counters, and the clock is deliberately NOT an input to assembly.
 */
export function recomputeRaffles(): void {
  const state = useAppStore.getState();
  const next = assembleRaffles(
    state.campaignsById,
    state.raffleProgressById,
    state.raffleProgressStream.subscribed
  );
  if (listEqual(state.raffles, next)) return;
  state.setRaffles(next);
}
